import "@/App.css";
import { Component, lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Home from "@/pages/Home";
import About from "@/pages/About";
import EPK from "@/pages/EPK";
import Contact from "@/pages/Contact";
import Music from "@/pages/Music";
import Tour from "@/pages/Tour";
import Community from "@/pages/Community";
import Shop from "@/pages/Shop";
import NewAlbum from "@/pages/NewAlbum";
import { TrackingPixel } from "@/components/site/TrackingPixel";
import { GlowCursor } from "@/components/site/GlowCursor";
import { SiteAudioProvider } from "@/components/site/SiteAudio";

// The site editor (/admin): its own screen, none of the site's effects.
// Its code is a separate file. A tab opened before a new deploy (every
// Publish makes one) asks for the old file, which no longer exists, so a
// failed load reloads the page once to pick up the current deploy.
const RELOADED_KEY = "editor-reloaded-at";
const AdminApp = lazy(() =>
  import("@/admin/AdminApp").catch((err) => {
    let last = 0;
    try {
      last = Number(sessionStorage.getItem(RELOADED_KEY)) || 0;
      if (Date.now() - last > 30000) {
        sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
        window.location.reload();
        return new Promise(() => {});
      }
    } catch {}
    throw err;
  })
);

const adminScreen = "flex min-h-screen flex-col items-center justify-center gap-4 bg-[#08090a] px-6 text-center text-white/70";

// If the editor still can't load (or crashes), say so instead of a black page.
class AdminBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className={adminScreen}>
        <p>The editor didn't load. This usually fixes itself with a reload.</p>
        <button type="button" onClick={() => window.location.reload()} className="rounded-full bg-white px-5 py-2.5 text-sm font-medium text-black">
          Reload
        </button>
      </div>
    );
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// Sierra's site-wide ambient layers (SacredGeometryField, WorldDim) and the
// /sync EPK are Sierra-specific and not carried over. /tour and /shop are
// routed but kept out of NAV_LINKS until they have real content (see
// data/site.js).
function Site() {
  const { pathname } = useLocation();
  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    return (
      <AdminBoundary>
        <Suspense fallback={<div className={adminScreen}>Loading the editor…</div>}>
          <Routes>
            <Route path="/admin/*" element={<AdminApp />} />
          </Routes>
        </Suspense>
      </AdminBoundary>
    );
  }
  return (
    <>
      <TrackingPixel />
      <GlowCursor />
      <SiteAudioProvider>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/epk" element={<EPK />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/music" element={<Music />} />
          <Route path="/tour" element={<Tour />} />
          <Route path="/community" element={<Community />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/new-album" element={<NewAlbum />} />
        </Routes>
      </SiteAudioProvider>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Site />
      </BrowserRouter>
    </div>
  );
}

export default App;
